import React from 'react';
import { Link } from 'react-router-dom';
import { Send, ScrollText, Camera, FileCheck, Star, MapPin, Calendar, Gift, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const LandingLent = () => {
  const weeklyJourney = [
    { week: "1", site: "Church of the Holy Sepulchre", desc: "Site of crucifixion and resurrection", image: "https://images.unsplash.com/photo-1595133885824-a3a598947576?w=200" },
    { week: "2", site: "Via Dolorosa", desc: "The path Jesus walked to Calvary", image: "https://images.unsplash.com/photo-1686950109008-5cfd6bdff925?w=200" },
    { week: "3", site: "Garden of Gethsemane", desc: "Where Jesus prayed before arrest", image: "https://images.unsplash.com/photo-1686950109448-afa891b6cc7e?w=200" },
    { week: "4", site: "Garden Tomb", desc: "Site of the empty tomb", image: "https://images.unsplash.com/photo-1642757551038-9ad1ae5f84aa?w=200" },
    { week: "5", site: "Western Wall", desc: "Judaism's holiest site", image: "https://images.unsplash.com/photo-1695781857590-9c1ee8847a8a?w=200" },
    { week: "6", site: "Mount of Olives", desc: "Where Jesus ascended to heaven", image: "https://images.unsplash.com/photo-1697747245806-6aa4b6565a54?w=200" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Lenten Prayer Program | 6 Weeks of Prayers Delivered to Jerusalem"
        description="This Lent, have your congregation's prayers printed on our Weekly Sacred Scroll and delivered to a different Jerusalem holy site each week. Photo proof included."
        path="/lent"
      />
      
      {/* Hero Section - Bright */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-64 h-64 bg-amber-200/50 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-orange-200/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-100/30 rounded-full blur-3xl" />
        
        <div className="relative z-10 container mx-auto px-5 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-100 border border-amber-200 rounded-full">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span className="text-amber-700 text-sm font-semibold">Lenten Prayer Program for Churches</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
              This Lent, Deliver Your<br />
              <span className="text-amber-600">Prayers to Jerusalem</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              6 weeks. 6 holy sites. Every week, your congregation's prayers are printed on our <strong className="text-amber-700">Weekly Sacred Scroll</strong> and delivered to a different sacred location in Jerusalem.
            </p>
            
            {/* Key Points */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {[
                { icon: ScrollText, text: "Printed on Sacred Scroll" },
                { icon: MapPin, text: "6 Different Holy Sites" },
                { icon: Camera, text: "Photo Proof Each Week" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-md border border-amber-100">
                  <item.icon className="w-5 h-5 text-amber-500" />
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
            
            <Link to="/contact">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-10 py-7 shadow-lg shadow-amber-500/25 transition-all" data-testid="hero-cta-btn">
                <Send className="mr-3" size={22} />
                Get Lent Program Pricing
              </Button>
            </Link>
            
            <p className="mt-4 text-gray-500 text-sm">
              Limited spots for 2025 Lent season
            </p>
          </div>
        </div>
      </section>

      {/* How Lent Program Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                How the Lent Program Works
              </h2>
              <p className="text-lg text-gray-600">Each week of Lent, a new holy site, new photos, new moment of faith</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: ScrollText, title: "Prayers Printed Weekly", desc: "Your congregation's prayers are printed on our Weekly Sacred Scroll each week during Lent", color: "from-amber-400 to-orange-500" },
                { icon: MapPin, title: "Delivered to 6 Sites", desc: "Each week, the scroll goes to a different holy site — from the Western Wall to the Garden Tomb", color: "from-blue-400 to-blue-600" },
                { icon: Camera, title: "Photos Every Week", desc: "Receive timestamped photos of the scroll at each location to share with your congregation", color: "from-green-400 to-emerald-600" }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center hover:shadow-xl transition-shadow">
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6 Week Journey - Bright Cards */}
      <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                Your 6-Week Journey
              </h2>
              <p className="text-lg text-gray-600">Each week, prayers delivered to a different sacred site</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {weeklyJourney.map((week, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-gray-100">
                  <div className="relative h-32">
                    <img src={week.image} alt={week.site} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold shadow-lg">
                        {week.week}
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mb-1">Week {week.week}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{week.site}</h3>
                    <p className="text-gray-500 text-sm">{week.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included - Bright */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                What Your Church Receives
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  Each Week During Lent
                </h3>
                <ul className="space-y-3">
                  {[
                    "Prayers printed on the Weekly Sacred Scroll",
                    "Scroll delivered to that week's holy site",
                    "Our Jerusalem messengers deliver your congregation's prayers",
                    "Timestamped photos sent to your church"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
                <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  At the End of Lent
                </h3>
                <ul className="space-y-3">
                  {[
                    "Delivery confirmation email for every participating family",
                    "Complete collection of all 6 weeks' photos",
                    "Materials to share at Easter service",
                    "Digital files for church displays"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial - Bright */}
      <section className="py-20 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-7 h-7 text-amber-400" fill="currentColor" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-gray-800 mb-8 italic leading-relaxed">
              "Every Sunday during Lent, we showed our congregation new photos of the Sacred Scroll at a different holy site. By Easter Sunday, when we shared the final photos from the Garden Tomb, it was incredibly powerful. Every family went home with the photos of their prayer's journey."
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                M
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900">Pastor Michael Thompson</p>
                <p className="text-amber-600">St. Mark's Lutheran Church, Wisconsin</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Bright Gradient */}
      <section className="py-24 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollText className="w-16 h-16 mx-auto mb-6 text-white" />
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6">
              Reserve Your Church's<br />Lenten Journey
            </h2>
            <p className="text-xl text-white/90 mb-10">
              6 weeks of prayers delivered to Jerusalem. Photo proof each week for every family.
            </p>
            
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-amber-600 text-xl px-12 py-8 shadow-2xl transition-all font-semibold" data-testid="final-cta-btn">
                <Send className="mr-3" size={24} />
                Get Lent Program Pricing
              </Button>
            </Link>
            
            <p className="mt-6 text-white/80 text-sm">
              Limited spots available • We respond within 24 hours
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-gray-900 text-center">
        <p className="text-gray-400 text-sm">
          © 2025 Jerusalem Holy Prayer • <Link to="/churches" className="hover:text-white">Church Programs</Link> • <Link to="/contact" className="hover:text-white">Contact</Link>
        </p>
      </footer>
    </div>
  );
};

export default LandingLent;
